<template>
  <div>
    <Navbar />
    <main>
      <section class="container py-5">
        <div v-if="user" class="row justify-content-md-center">
          <div class="col-md-4 text-center">
            You are already signed in as <strong>{{ user.email }}</strong>
          </div>
        </div>
        <div v-else>
          <div id="firebaseui-auth-container"></div>
          <div class="row justify-content-md-center mt-4">
            <div v-if="!user && !showVerificationInput && !ui">
              <LoaderPic />
            </div>
            <div class="col-md-4" v-if="!user && !showVerificationInput && !hints.length && ui">
              <strong>Don't have an account yet?</strong>
              Enter your email, and your account will be automatically created when you follow one-time authentication
              link.
              <span>
                By submitting the form, you accept the
                <router-link to="/privacy">Privacy Notice</router-link> of this website
                and the <router-link to="/terms">Terms of Service</router-link>.
              </span>
            </div>
            <div v-if="showVerificationInput" class="col-md-4 form-group text-center mt-4">
              <label for="verificationCode" class="font-weight-bold">{{ verificationPrompt }}</label>
              <div class="d-flex justify-content-center">
                <input id="verificationCode" autocomplete="off" type="text" v-model="verificationCode"
                  class="form-control w-200" placeholder="Enter your verification code" />
              </div>
              <button class="btn btn-primary mt-2" @click="submitVerificationCode">Submit</button>
              <div class="mt-2" v-for="option in otherFactors" :key="option.hint.uid">
                <button class="btn btn-link btn-sm" :disabled="sendingCode" @click="switchFactor(option.index)">{{ switchLabel(option.hint) }}</button>
              </div>
            </div>
          </div>
        </div>
        <div id="recaptcha-container"></div>
        <div ref="smsRecaptcha"></div>
      </section>

      <AsideFooter />
    </main>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import firebase from 'firebase/compat/app'
import { TotpMultiFactorGenerator } from 'firebase/auth'
import * as firebaseui from 'firebaseui'
import { firebaseAuth } from '../firebase.js'
import AsideFooter from '../components/AsideFooter.vue'
import Navbar from '../components/Navbar.vue'
import LoaderPic from '../components/elements/Loader/LoaderPic.vue'
import metadata from '../mixins/metadata.js'

export default {
  name: 'sign-in',
  data() {
    return {
      ui: null,
      showVerificationInput: false,
      verificationCode: '',
      verificationId: '',
      resolver: null,
      hints: [],
      selectedFactor: null,
      sendingCode: false,
      recaptchaVerifier: null
    }
  },
  mixins: [metadata],
  components: {
    AsideFooter,
    Navbar,
    LoaderPic
  },
  beforeDestroy() {
    document.removeEventListener('MFARequired', this.onMFARequired)
    if (this.ui) {
      this.ui.delete() // Clean up AuthUI instance
    }
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear() // Clear the reCAPTCHA verifier
    }
  },
  computed: {
    ...mapGetters(['user']),
    otherFactors() {
      return this.hints
        .map((hint, index) => ({ hint, index }))
        .filter(({ hint }) => !this.selectedFactor || hint.uid !== this.selectedFactor.uid)
    },
    verificationPrompt() {
      if (this.selectedFactor && this.selectedFactor.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
        return 'Enter the 6-digit code from your authenticator app'
      }
      return this.selectedFactor && this.selectedFactor.phoneNumber
        ? `Enter the code we sent to ${this.selectedFactor.phoneNumber}`
        : 'Enter the code we sent you by SMS'
    }
  },
  mounted() {
    document.addEventListener('MFARequired', this.onMFARequired)
    this.setupRecaptcha()
  },
  methods: {
    ...mapActions(['pushAlertMessage', 'resolveMFA']),
    setupRecaptcha() {
      this.recaptchaVerifier = new firebase.auth.RecaptchaVerifier('recaptcha-container', {
        size: 'invisible',
        callback: () => {
          // reCAPTCHA solved, show Firebase UI
          console.log('reCAPTCHA solved!')
          this.$nextTick(() => {
            this.initializeFirebaseUI()
          })
        }
      })
      this.recaptchaVerifier.verify()
    },
    initializeFirebaseUI() {
      if (!this.ui) {
        this.ui = new firebaseui.auth.AuthUI(firebaseAuth)
        this.ui.start('#firebaseui-auth-container', {
          signInOptions: [
            {
              provider: firebase.auth.EmailAuthProvider.PROVIDER_ID,
              signInMethod: firebase.auth.EmailAuthProvider.EMAIL_LINK_SIGN_IN_METHOD
            }
          ],
          signInSuccessUrl: new URLSearchParams(window.location.search).get('next') || '/cp'
        })
      }
    },
    onMFARequired(event) {
      this.handleMFA(event.detail)
    },
    async handleMFA(error) {
      this.resolver = error.resolver // Store the resolver
      this.hints = this.resolver.hints

      // Prefer the authenticator app; the other method stays one click away
      const totpIndex = this.hints.findIndex(hint => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID)
      this.selectFactor(totpIndex !== -1 ? totpIndex : 0)
    },
    switchLabel(hint) {
      if (hint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
        return 'Use your authenticator app instead'
      }
      return this.sendingCode ? 'Sending code...' : 'Send a code by SMS instead'
    },
    async selectFactor(index) {
      const hint = this.hints[index]

      // A TOTP code is already in the user's authenticator app, nothing to send
      if (hint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
        this.selectedFactor = hint
        this.showVerificationInput = true
        return
      }

      const phoneInfoOptions = {
        multiFactorHint: hint,
        session: this.resolver.session
      }
      const phoneAuthProvider = new firebase.auth.PhoneAuthProvider()

      // A fresh element per send: an invisible verifier renders into its container
      // and clear() leaves the widget behind, so a container cannot be reused
      const element = document.createElement('div')
      this.$refs.smsRecaptcha.appendChild(element)
      const verifier = new firebase.auth.RecaptchaVerifier(element, { size: 'invisible' })
      this.sendingCode = true

      try {
        // Send SMS verification code
        this.verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, verifier)
        this.selectedFactor = hint
        this.showVerificationInput = true // Show the input for verification code
      } catch (error) {
        // The current method, if any, stays selected, e.g. when SMS is disabled for the project
        this.pushAlertMessage({
          message: error.message || 'Error during MFA resolution'
        })
        console.error('Error during MFA resolution:', error)
      } finally {
        this.sendingCode = false
        verifier.clear()
        element.remove()
      }
    },
    switchFactor(index) {
      this.verificationCode = ''
      this.selectFactor(index)
    },
    async submitVerificationCode() {
      try {
        await this.resolveMFA({
          resolver: this.resolver,
          factorId: this.selectedFactor.factorId,
          hintUid: this.selectedFactor.uid,
          verificationId: this.verificationId,
          verificationCode: this.verificationCode
        })
        this.showVerificationInput = false // Hide input after submission
        this.verificationCode = '' // Clear the input
        this.$router.push(new URLSearchParams(window.location.search).get('next') || '/cp')
      } catch (error) {
        this.pushAlertMessage({
          message: 'Error during sign-in with second factor'
        })
        console.error('Error during sign-in with second factor:', error)
      }
    }
  }
}
</script>
